import mixpanel from "mixpanel-browser";

export type AuthMethod = "google" | "email" | "apple";

type TrackProps = Record<string, string | number | boolean | null | undefined>;

let initialized = false;

const onceKeys = new Set<string>();

function storageGet(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function storageSet(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // private mode / blocked storage
  }
}

/** Web only for now; apps can pass platform later via the same helper API. */
export function getAnalyticsPlatform(): "web" {
  return "web";
}

export function ensureMixpanel(): boolean {
  if (!process.env.REACT_APP_MIXPANEL_ID || !navigator.onLine) {
    return false;
  }
  if (!initialized) {
    mixpanel.init(process.env.REACT_APP_MIXPANEL_ID, {
      batch_requests: true,
    });
    initialized = true;
  }
  return true;
}

function baseProps(extra?: TrackProps): TrackProps {
  return {
    platform: getAnalyticsPlatform(),
    ...extra,
  };
}

/** Fire at most once per browser tab session for a given key. */
export function trackOnce(event: string, key: string, props?: TrackProps): void {
  if (!ensureMixpanel()) return;
  const storageKey = `mp_once:${key}`;
  if (onceKeys.has(storageKey) || storageGet(storageKey)) return;
  onceKeys.add(storageKey);
  storageSet(storageKey, "1");
  mixpanel.track(event, baseProps(props));
}

export function trackEvent(event: string, props?: TrackProps): void {
  if (!ensureMixpanel()) return;
  mixpanel.track(event, baseProps(props));
}

export function identifyUser(user: {
  id: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  freeTrial?: boolean;
  userPlan?: { name?: string } | null;
  reseller?: { name?: string } | null;
  hasCompany?: boolean;
  authMethod?: AuthMethod;
}): void {
  if (!ensureMixpanel() || !user?.id) return;

  mixpanel.identify(String(user.id));
  mixpanel.people.set({
    $email: user.email,
    $first_name: user.firstName,
    $last_name: user.lastName,
    plan: user.userPlan?.name,
    reseller: user.reseller?.name,
    freetrial: user.freeTrial,
    has_company: Boolean(user.hasCompany),
    signup_platform: getAnalyticsPlatform(),
  });

  if (user.authMethod) {
    mixpanel.people.set_once({
      first_auth_method: user.authMethod,
      signup_platform: getAnalyticsPlatform(),
    });
  }
}

/** New account if freeTrialDate is within the last few minutes (Google/Apple create path). */
export function looksLikeNewSignup(freeTrialDate?: string | null): boolean {
  if (!freeTrialDate) return false;
  const created = new Date(freeTrialDate).getTime();
  if (Number.isNaN(created)) return false;
  return Date.now() - created < 5 * 60 * 1000;
}

export function trackAuthSuccess(params: {
  userId: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  freeTrial?: boolean;
  freeTrialDate?: string | null;
  authMethod: AuthMethod;
  /** Force Signed Up (e.g. email register form). */
  isNewUser?: boolean;
}): void {
  const isNew =
    params.isNewUser === true || looksLikeNewSignup(params.freeTrialDate);

  identifyUser({
    id: params.userId,
    email: params.email,
    firstName: params.firstName,
    lastName: params.lastName,
    freeTrial: params.freeTrial,
    authMethod: params.authMethod,
    hasCompany: false,
  });

  trackOnce(
    isNew ? "Signed Up" : "Logged In",
    `auth:${params.userId}:${isNew ? "signup" : "login"}`,
    { auth_method: params.authMethod }
  );
}
